import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import AiModelsTab from './AiModelsTab';
import { UsageCard } from '../UsageCard';
import * as api from '../../../services/api';

/**
 * LlmOperationsTab — Admin Center "LLM Operations".
 *
 * One place for a workspace's model operations: connect a provider once and the
 * same key powers the builder, agent inference, and runtime (governed + metered).
 * Composes existing surfaces rather than duplicating them:
 *   • {@link AiModelsTab} — providers / connector-backed models, capability routing,
 *     and per-capability cost controls (max tokens, temperature, fallback), per scope.
 *   • {@link UsageCard} (self-serve) — this workspace's current-month token + cost usage,
 *     with the billable-vs-BYOK split and the managed-LLM budget bar.
 *
 * The budget is read from the caller's own entitlement summary (soft limit of the
 * LLM_COST_USD quota); bring-your-own-key spend is billed by the provider and never
 * counts against it.
 */
export default function LlmOperationsTab() {
  const [budgetUsd, setBudgetUsd] = useState(null);
  const [billingMode, setBillingMode] = useState(null); // effective Anthropic mode: 'MANAGED' | 'BYOK'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getMyEntitlements();
        const data = res && res.data ? res.data : res;
        const quota = (data?.quotas || []).find((q) => q.key === 'LLM_COST_USD');
        if (!cancelled) setBudgetUsd(quota?.soft ?? null);
      } catch (_) { /* budget is optional — the usage card renders without it */ }
      try {
        const res = await api.getMyLlmBillingMode();
        const data = res && res.data ? res.data : res;
        if (!cancelled) setBillingMode(data?.anthropic ?? null);
      } catch (_) { /* mode indicator is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      <div className="mb-4 text-[12px] text-v2-ink-secondary leading-relaxed">
        Connect a model provider once — the same key powers your workflow builder, agent
        inference, and runtime, protected and metered per workspace.
      </div>

      {/* Providers, capability routing, and cost controls (reused AI Models surface). */}
      <AiModelsTab />

      {/* This workspace's usage and cost — tokens, billable vs bring-your-own-key, and budget. */}
      <div className="mt-6 mb-2 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-v2-ink">Usage &amp; cost</span>
        {billingMode && (
          <span className={`text-[11px] px-2 py-0.5 rounded-v2-sm border ${
            billingMode === 'MANAGED'
              ? 'text-v2-action border-v2-action/40 bg-v2-action/5'
              : 'text-v2-ink-secondary border-v2-border-subtle bg-v2-muted'}`}>
            Billing: {billingMode === 'MANAGED' ? 'Managed (invoiced)' : 'BYOK (not invoiced)'}
          </span>
        )}
      </div>
      <UsageCard selfServe budgetUsd={budgetUsd} />

      <div className="mt-4 flex items-start gap-2 rounded-v2-sm border border-v2-border-subtle bg-v2-muted px-3 py-2 text-[12px] text-v2-ink-secondary">
        <ShieldCheck size={13} className="mt-0.5 flex-shrink-0 text-v2-action-strong" />
        <span>
          Every model call routes through the same policy checks and audit trail as any
          protected action — connecting a key doesn’t bypass protection.
        </span>
      </div>
    </div>
  );
}
